import { WindowRef } from '../../../Apps/Models/WindowRef';
import { ActionTypes, AuthContextFDReadyEvent, ClientSessionContextReadyEvent } from '../../../Common/Constants';
import { ErrorTypes } from '../../../Common/Constants/ErrorTypes.Constants';
import { EventGroupType } from '../../../Common/Constants/EventGroupType.Constants';
import { isEmptyOrSpaces } from '../../../Utilities/Helpers';
import HttpRequestService from '../HttpService/HttpRequestService';
import TelemetryService from '../Telemetry/TelemetryService';
import {
  AuthContextApi,
  AuthContextData,
  ClientSessionContextApi,
  ClientSessionContextData,
} from './Models/ClientSessionContext';

const enum ContextType {
  ClientContext = 'clientSessionContext',
  AuthContext = 'authContext',
}

export class ClientSessionContext {
  public static instance: ClientSessionContext;

  private clientSessionContextGlobalPromise: Promise<ClientSessionContextApi | null> | undefined;
  private authContextGlobalPromise: Promise<AuthContextData | null> | undefined;
  private telemetryService: TelemetryService = TelemetryService.getInstance();

  public static getInstance = () => {
    if (!ClientSessionContext.instance) {
      ClientSessionContext.instance = new ClientSessionContext();
    }
    return ClientSessionContext.instance;
  };

  private constructor() {
    this.setupClientContextGlobalPromise();
  }

  // Everybody should be dependent on this function to get the client Context.
  public getContext = () => {
    return this.clientSessionContextGlobalPromise as Promise<ClientSessionContextData | null>;
  };

  public getAuthContext = () => {
    return this.authContextGlobalPromise as Promise<AuthContextData | null>;
  };

  // This is used to update the client Token.
  public updateClientToken = (clientToken: string) => {
    if (isEmptyOrSpaces(clientToken)) {
      return false;
    }

    this.authContextGlobalPromise?.then((authContext: AuthContextData | null) => {
      if (authContext) {
        authContext.clientToken = clientToken;
      }
    });
  };

  // Get Client Context or Auth Context from the window object.
  private checkContextObjectInWindow = (contextType: ContextType) => {
    const contextResponse =
      contextType === ContextType.ClientContext
        ? (window as WindowRef).Support.clientContextResponse
        : (window as WindowRef).Support.authContextResponse;

    if (contextResponse) {
      if (contextResponse.data && contextResponse.status === 200) {
        this.telemetryService.traceEvent(
          ActionTypes.clientContextSuccess.replace('{0}', contextType),
          {},
          EventGroupType.api
        );
        return contextResponse.data;
      } else {
        this.telemetryService.traceError(new Error(ErrorTypes.clientContextFailed.replace('{0}', contextType)), {
          status: contextResponse.status,
          error: contextResponse.error ?? '',
        });
      }
    }
    return null;
  };

  // Creates a promise for the global call that was sent when the html was loaded. It also handles the
  // retry logic for each of this api call.
  private getClientContextPromise = (contextType: ContextType) => {
    const contextPromise = new Promise((resolve: (value: ClientSessionContextApi | AuthContextApi | null) => void) => {
      const contextGlobal = this.checkContextObjectInWindow(contextType);
      if (contextGlobal) {
        resolve(contextGlobal);
        return;
      }
      const eventCallback = async () => {
        const clientContextGlobal = this.checkContextObjectInWindow(contextType);
        if (clientContextGlobal) {
          resolve(clientContextGlobal);
          return;
        }

        try {
          // Indicating the global call failed for the given context.
          // In this case we need to retry.
          const httpServiceInstance = HttpRequestService.getInstance();
          const clientContextRetry =
            contextType === ContextType.ClientContext
              ? await httpServiceInstance.getAuthContextFd()
              : await httpServiceInstance.getAuthContextFd();

          resolve(clientContextRetry);
        } catch (error) {
          this.telemetryService.traceError(new Error(ErrorTypes.clientContextFailed.replace('{0}', contextType)), {
            source: 'Client Context Retry Failed',
          });
        }
      };

      const eventToListen =
        contextType === ContextType.ClientContext ? ClientSessionContextReadyEvent : AuthContextFDReadyEvent;
      document.addEventListener(eventToListen, eventCallback);
    });
    return contextPromise;
  };

  // This is responsible for creating the global promise that will be shared other services which can wait while the data is
  // being fetched. It sets up global promise for both client session context which is the call made to partner center support
  // service to get token along with other information such as sessionIds , correlationIds.
  // It also sets up global promise for the auth token, since there are 2 possible sources it makes sense to create a separate
  // for the token allowing components to load if they are only interested in the token.
  private setupClientContextGlobalPromise = (): void => {
    this.clientSessionContextGlobalPromise = this.getClientContextPromise(
      ContextType.ClientContext
    ) as Promise<ClientSessionContextApi | null>;

    const clientTokenResponse: AuthContextData = { clientToken: '' };
    let isClientSessionContextRejected: boolean = false;
    let isAuthContextRejected: boolean = false;
    // This is the auth Context Promise which uses both the api calls to return the data for clientToken.
    // It allows us to select whichever is fastet. By pattern FD call should be faster because it will only involve 1 hop
    // but we can still use the client context information if it is available.
    this.authContextGlobalPromise = new Promise((resolve: (value: AuthContextData | null) => void) => {
      const authContextFdPromise = this.getClientContextPromise(
        ContextType.AuthContext
      ) as Promise<AuthContextApi | null>;

      authContextFdPromise
        .then(authContextData => {
          const partnerCenterAppToken = authContextData?.accountsFirstPartyApp ?? authContextData?.partnerCenterAppToken ?? authContextData?.PartnerCenterApp ?? '';
          if (isEmptyOrSpaces(partnerCenterAppToken)) {
            throw new Error('authContextData AppToken is empty');
          }
          clientTokenResponse.clientToken = `Bearer ${partnerCenterAppToken}`;
          resolve(clientTokenResponse);
        })
        .catch(error => {
          isAuthContextRejected = true;
          console.log("failed to resolve auth context token", error);
          if (isClientSessionContextRejected) {
            resolve(null);
            return;
          }
        });
      this.clientSessionContextGlobalPromise
        ?.then(clientSessionContext => {
          if (
            isEmptyOrSpaces(clientTokenResponse.clientToken) &&
            clientSessionContext?.clientToken &&
            !isEmptyOrSpaces(clientSessionContext?.clientToken)
          ) {
            clientTokenResponse.clientToken = clientSessionContext.clientToken;
            resolve(clientTokenResponse);
            return;
          }
        })
        .catch(error => {
          isClientSessionContextRejected = true;
          if (isAuthContextRejected) {
            resolve(null);
            return;
          }
        });
    });
  };
}
